import { Bookmark } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import { supabase } from '../../../../lib/supabase';
import type { Job } from '../JobCard';
import JobCard from '../JobCard';

import { cleanSkills } from './helpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal row shape from public.saved_jobs */
type SavedRow = {
  source: string;
  external_id: string;
  url: string;
  title: string | null;
  company: string | null;
  saved_at: string;
};

/** Full row shape from public.peerlist_jobs */
type PeerlistRow = {
  external_slug: string;
  url: string;
  title: string;
  company: string;
  company_logo_url: string | null;
  location: string | null;
  remote: boolean | null;
  job_type: string | null;
  experience_level: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  skills: string[] | null;
  must_have_skills: string[] | null;
  good_to_have_skills: string[] | null;
  duration: string | null;
  compensation_text: string | null;
  description_md: string | null;
  posted_at: string | null;
  last_seen_at: string;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SavedJobsListProps {
  savedKeys: Set<string>;
  onToggleSave: (job: Job) => void;
  onApply:      (job: Job) => void;
  onView:       (job: Job) => void;
  /** Forwarded to JobCard so clicks open the Job Detail drawer in Jobs.tsx. */
  onCardClick?: (job: Job) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a Job from full peerlist data, falling back to the snapshot fields
 * stored in saved_jobs when the job is no longer active in peerlist_jobs.
 */
function buildJob(saved: SavedRow, full?: PeerlistRow): Job {
  if (full) {
    const skills       = cleanSkills(full.skills);
    const mustHave     = cleanSkills(full.must_have_skills);
    const goodToHave   = cleanSkills(full.good_to_have_skills);
    return {
      id:                  full.external_slug,
      source:              'peerlist',
      title:               full.title,
      company:             full.company,
      company_logo_url:    full.company_logo_url ?? undefined,
      location:            full.location ?? '',
      url:                 full.url,
      created:             full.last_seen_at,
      posted_at:           full.posted_at ?? undefined,
      description:         full.description_md ?? undefined,
      skills:              skills.length > 0 ? skills : undefined,
      must_have_skills:    mustHave.length > 0 ? mustHave : undefined,
      good_to_have_skills: goodToHave.length > 0 ? goodToHave : undefined,
      duration:            full.duration ?? undefined,
      compensation_text:   full.compensation_text ?? undefined,
      salary_min:          full.salary_min ?? undefined,
      salary_max:          full.salary_max ?? undefined,
      salary_currency:     full.salary_currency ?? 'INR',
      job_type:            full.job_type ?? undefined,
      experience_level:    full.experience_level ?? undefined,
      remote:              full.remote ?? undefined,
      external_job_id:     full.external_slug,
    };
  }

  // Fallback: build a minimal card from the saved snapshot
  return {
    id:              saved.external_id,
    source:          saved.source as Job['source'],
    title:           saved.title ?? 'Unknown job',
    company:         saved.company ?? 'Unknown company',
    location:        '',
    url:             saved.url,
    created:         saved.saved_at,
    external_job_id: saved.external_id,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SavedJobsList: React.FC<SavedJobsListProps> = ({
  savedKeys,
  onToggleSave,
  onApply,
  onView,
  onCardClick,
}) => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const load = async () => {
      // 1. Fetch the user's saved rows (RLS scopes to auth.uid())
      const { data: savedRows, error: savedErr } = await supabase
        .from('saved_jobs')
        .select('source, external_id, url, title, company, saved_at')
        .order('saved_at', { ascending: false })
        .limit(500);

      if (!mounted) return;
      if (savedErr || !savedRows?.length) {
        setJobs([]);
        setLoading(false);
        return;
      }

      // 2. Fetch full Peerlist rows for the saved slugs (active or not)
      const peerlistSlugs = savedRows
        .filter(r => r.source === 'peerlist')
        .map(r => r.external_id as string);

      let fullMap = new Map<string, PeerlistRow>();

      if (peerlistSlugs.length > 0) {
        const { data: fullRows } = await supabase
          .from('peerlist_jobs')
          .select(
            'external_slug, url, title, company, company_logo_url, location, ' +
            'remote, job_type, experience_level, salary_min, salary_max, ' +
            'salary_currency, skills, must_have_skills, good_to_have_skills, ' +
            'duration, compensation_text, description_md, posted_at, last_seen_at',
          )
          .in('external_slug', peerlistSlugs);

        if (fullRows) {
          for (const row of fullRows) {
            fullMap.set(row.external_slug as string, row as PeerlistRow);
          }
        }
      }

      if (!mounted) return;

      // 3. Build Job objects preserving saved_at order
      const builtJobs = savedRows.map(saved =>
        buildJob(saved as SavedRow, fullMap.get(saved.external_id as string)),
      );

      setJobs(builtJobs);
      setLoading(false);
    };

    load();
    return () => { mounted = false; };
  // Re-fetch when the saved set changes so unsave removes the card immediately
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKeys.size]);

  if (loading) {
    return (
      <div className="ds-card flex items-center gap-3">
        <LoadingSpinner size="sm" showText={false} />
        <span className="t-body">Loading saved jobs…</span>
      </div>
    );
  }

  // Filter out any jobs whose key is no longer in savedKeys (optimistic unsave)
  const visible = jobs.filter(j =>
    savedKeys.has(`${j.source}:${j.external_job_id ?? j.id}`),
  );

  if (visible.length === 0) {
    return (
      <div className="ds-card p-8 text-center">
        <Bookmark className="w-12 h-12 mx-auto mb-3 opacity-60" style={{ color: 'var(--ds-text-muted)' }} />
        <p className="t-card-title mb-1">No saved jobs yet</p>
        <p className="t-meta">
          Tap the bookmark on any card to save it here.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {visible.map(job => (
        <JobCard
          key={`${job.source}:${job.external_job_id ?? job.id}`}
          job={job}
          isSaved={true}
          onToggleSave={onToggleSave}
          onApply={onApply}
          onView={onView}
          onCardClick={onCardClick}
        />
      ))}
    </div>
  );
};

export default React.memo(SavedJobsList);
